import { Link, Outlet } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { PageHeader } from './shell.js';

const NAV_GROUPS: Array<{ label: string; items: Array<{ to: string; label: string }> }> = [
  {
    label: 'Organisation',
    items: [
      { to: '/admin', label: 'Overview' },
      { to: '/admin/general', label: 'General & company' },
      { to: '/admin/branding', label: 'Branding' },
    ],
  },
  {
    label: 'Access',
    items: [
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/roles', label: 'Roles & permissions' },
      { to: '/admin/teams', label: 'Teams' },
      { to: '/admin/security', label: 'Security' },
      { to: '/admin/sso', label: 'Single sign-on' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { to: '/admin/leads', label: 'Leads' },
      { to: '/admin/web-forms', label: 'Web forms' },
      { to: '/admin/pipelines', label: 'Pipelines & stages' },
      { to: '/admin/tags', label: 'Tags' },
      { to: '/admin/custom-fields', label: 'Custom fields' },
    ],
  },
  {
    label: 'Communication',
    items: [
      { to: '/admin/email', label: 'Email' },
      { to: '/admin/notifications', label: 'Notifications' },
    ],
  },
  {
    label: 'Data & compliance',
    items: [
      { to: '/admin/retention', label: 'Data retention' },
      { to: '/admin/audit-log', label: 'Audit log' },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { to: '/admin/integrations/inbox-pulse', label: 'Inbox Pulse' },
      { to: '/admin/integrations/calling-meetings', label: 'Calling & meetings' },
      { to: '/admin/integrations/ai-qualification', label: 'AI lead qualification' },
      { to: '/admin/integrations/whatsapp', label: 'WhatsApp' },
    ],
  },
  {
    label: 'Developers',
    items: [{ to: '/admin/webhooks', label: 'API keys & webhooks' }],
  },
];

/** docs section 5 "Administrator Control" — the admin sub-nav shell.
 * Permission checks happen per-page against the real endpoint (a 403 from
 * the API, not just hiding a link, is what actually protects each screen);
 * this nav shows every section since only admins reach `/admin` at all in
 * practice (the top-level sidebar item itself isn't gated yet — see
 * docs/architecture/12-implementation-status.md). */
/** `children` overrides `<Outlet/>` for the `/__design/:screen` preview
 * harness — same reasoning as `AppShell`'s own `children` prop. */
export function AdminShell({ children }: { children?: ReactNode } = {}) {
  return (
    <div className="flex min-h-0 flex-1">
      <nav aria-label="Administration" className="flex w-60 shrink-0 flex-col gap-4 overflow-y-auto border-r border-border bg-surface px-3 py-4.5">
        <span className="px-2 pb-3 text-base font-[650] tracking-[-0.01em]">Administration</span>
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="flex flex-col gap-0.5">
            <span className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">{group.label}</span>
            {group.items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{ exact: item.to === '/admin' }}
                activeProps={{ className: 'bg-primary-soft text-primary-soft-text font-semibold' }}
                inactiveProps={{ className: 'text-text-secondary font-medium hover:bg-surface-subtle' }}
                className="flex h-6.75 items-center rounded-sm px-2 text-[13px]"
              >
                {item.label}
              </Link>
            ))}
          </div>
        ))}
      </nav>
      <div className="flex min-w-0 flex-1 flex-col">{children ?? <Outlet />}</div>
    </div>
  );
}

export { PageHeader };
